import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Icon } from "@iconify/react/dist/iconify.js";
import { apiCall } from "../utils/apiService";
import { previewInboxChime } from "../utils/inboxSound";
import ToggleBar from "./child/ToggleBar";
import "./SettingLayer.css";

const parsePlanName = (plan) => {
  try {
    const p = typeof plan === "string" ? JSON.parse(plan) : plan;
    return p?.name || "Free";
  } catch {
    return "Free";
  }
};

const initials = (name = "") => {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return (parts[0]?.[0] || "U").toUpperCase();
};

const SettingLayer = () => {
  const [toast, setToast] = useState(null);
  const [saving, setSaving] = useState(false);
  const [user, setUser] = useState(null);
  const [setting, setSetting] = useState({
    isAddQueueLog: false,
    allowedDuplicate: false,
    pin_instance_send: false,
    chat_enabled: true,
    inbox_sound: true,
    enter_to_send: true,
  });
  const [alerts, setAlerts] = useState({
    alert_in_app: true,
    alert_disconnect: true,
    alert_campaign_fail: true,
    alert_email: "",
  });
  const [security, setSecurity] = useState({
    name: "",
    email: "",
    newpass: "",
    confirm: "",
  });

  const showToast = (message, cls = "success") => {
    setToast({ message, class: cls });
    setTimeout(() => setToast(null), 2400);
  };

  const mergeLocalUser = (patch) => {
    const stored = JSON.parse(localStorage.getItem("user") || "{}");
    const next = { ...stored, ...patch };
    localStorage.setItem("user", JSON.stringify(next));
    setUser(next);
    return next;
  };

  const loadUser = async () => {
    try {
      const res = await apiCall({ url: "user/get-user-by-token", method: "GET" });
      const data = res?.data || JSON.parse(localStorage.getItem("user") || "{}");
      if (data) {
        mergeLocalUser(data);
        setSetting({
          isAddQueueLog: !!Number(data.isAddQueueLog),
          allowedDuplicate: !!Number(data.allowedDuplicate),
          pin_instance_send: !!Number(data.pin_instance_send),
          chat_enabled: data.chat_enabled !== 0 && data.chat_enabled !== false,
          inbox_sound: data.inbox_sound === undefined ? true : !!Number(data.inbox_sound),
          enter_to_send: data.enter_to_send === undefined ? true : !!Number(data.enter_to_send),
        });
        setSecurity({
          name: data.name || "",
          email: data.email || "",
          newpass: "",
          confirm: "",
        });
      }
    } catch {
      const stored = JSON.parse(localStorage.getItem("user") || "{}");
      setUser(stored);
      setSetting({
        isAddQueueLog: !!stored.isAddQueueLog,
        allowedDuplicate: !!stored.allowedDuplicate,
        pin_instance_send: !!stored.pin_instance_send,
        chat_enabled: stored.chat_enabled !== 0 && stored.chat_enabled !== false,
        inbox_sound: stored.inbox_sound === undefined ? true : !!stored.inbox_sound,
        enter_to_send: stored.enter_to_send === undefined ? true : !!stored.enter_to_send,
      });
      setSecurity({
        name: stored.name || "",
        email: stored.email || "",
        newpass: "",
        confirm: "",
      });
    }
  };

  const loadAlerts = async () => {
    try {
      const res = await apiCall({ url: "features/alerts", method: "GET" });
      if (res?.success && res.data) {
        setAlerts({
          alert_in_app: res.data.alert_in_app === undefined ? true : !!Number(res.data.alert_in_app),
          alert_disconnect: !!Number(res.data.alert_disconnect),
          alert_campaign_fail: res.data.alert_campaign_fail === undefined ? true : !!Number(res.data.alert_campaign_fail),
          alert_email: res.data.alert_email || "",
        });
      }
    } catch (error) {
      console.error("Error loading alert settings:", error);
    }
  };

  const saveSettings = async (nextSetting) => {
    setSaving(true);
    try {
      const response = await apiCall({
        url: "user/update-profile",
        method: "POST",
        body: {
          isAddQueueLog: nextSetting.isAddQueueLog,
          allowedDuplicate: nextSetting.allowedDuplicate,
          pin_instance_send: nextSetting.pin_instance_send,
          chat_enabled: nextSetting.chat_enabled,
          inbox_sound: nextSetting.inbox_sound,
          enter_to_send: nextSetting.enter_to_send,
        },
      });
      if (response.success) {
        mergeLocalUser({ ...nextSetting, ...(response.user || {}) });
        showToast(response.msg || "Settings saved");
      } else {
        showToast(response.msg || "Failed to save settings", "danger");
      }
    } catch (error) {
      showToast(error.message, "danger");
    } finally {
      setSaving(false);
    }
  };

  const saveAlerts = async (next) => {
    setSaving(true);
    try {
      const response = await apiCall({
        url: "features/alerts/update",
        method: "POST",
        body: next,
      });
      if (response.success) showToast(response.msg || "Alert settings saved");
      else showToast(response.msg || "Failed to save alerts", "danger");
    } catch (error) {
      showToast(error.message, "danger");
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = (key, checked) => {
    const next = { ...setting, [key]: checked };
    setSetting(next);
    saveSettings(next);
    // Toggle click unlocks AudioContext; play a preview so the user hears it works.
    if (key === "inbox_sound" && checked) {
      previewInboxChime();
    }
  };

  const handleAlertToggle = (key, checked) => {
    const next = { ...alerts, [key]: checked };
    setAlerts(next);
    saveAlerts(next);
  };

  const saveAccount = async (e) => {
    e.preventDefault();
    if (security.newpass && security.newpass !== security.confirm) {
      showToast("Passwords do not match", "danger");
      return;
    }
    if (security.newpass && security.newpass.length < 6) {
      showToast("Password must be at least 6 characters", "danger");
      return;
    }
    setSaving(true);
    try {
      const body = {
        name: security.name.trim(),
        email: security.email.trim(),
      };
      if (security.newpass) body.newpass = security.newpass;
      const response = await apiCall({
        url: "user/update-profile",
        method: "POST",
        body,
      });
      if (response.success) {
        mergeLocalUser({
          name: security.name.trim(),
          email: security.email.trim(),
          ...(response.user || {}),
        });
        setSecurity((s) => ({ ...s, newpass: "", confirm: "" }));
        showToast(response.msg || "Account updated");
      } else {
        showToast(response.msg || "Could not update account", "danger");
      }
    } catch (error) {
      showToast(error.message, "danger");
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    document.title = "Settings";
    loadUser();
    loadAlerts();
  }, []);

  const planName = parsePlanName(user?.plan);
  const msgLeft = user?.msglimit ?? "—";
  const avatarUrl = user?.image
    ? `${process.env.REACT_APP_FILE_URL}/profile/${user.image}`
    : null;

  return (
    <div className="settings-page">
      {toast && (
        <div className={`alert alert-${toast.class} settings-toast`}>
          {saving ? "Saving…" : toast.message}
        </div>
      )}

      <section className="settings-hero">
        <div className="settings-hero-main">
          <div
            className="settings-avatar"
            style={avatarUrl ? { backgroundImage: `url(${avatarUrl})` } : undefined}
          >
            {!avatarUrl && initials(user?.name)}
          </div>
          <div>
            <h2>{user?.name || "Your workspace"}</h2>
            <p>Control messaging, inbox behavior, and alerts for your account.</p>
            <div className="settings-chips">
              <span className="settings-chip">
                <Icon icon="mdi:crown-outline" width={15} />
                {planName}
              </span>
              <span className="settings-chip">
                <Icon icon="mdi:message-badge-outline" width={15} />
                {msgLeft} messages left
              </span>
              <span className="settings-chip">
                <Icon icon="mdi:email-outline" width={15} />
                {user?.email || "—"}
              </span>
            </div>
          </div>
        </div>
      </section>

      <div className="settings-grid">
        <div className="settings-stack">
          <section className="settings-card">
            <div className="settings-card-head">
              <div className="settings-card-icon teal">
                <Icon icon="mdi:send-clock-outline" width={22} />
              </div>
              <div>
                <h3>Messaging & delivery</h3>
                <p>How outbound messages are queued and deduplicated.</p>
              </div>
            </div>
            <ToggleBar
              id="set-queue"
              label="Use queue system"
              description="Queue campaign / bulk sends instead of firing everything at once."
              checked={!!setting.isAddQueueLog}
              disabled={saving}
              onChange={(checked) => handleToggle("isAddQueueLog", checked)}
            />
            <ToggleBar
              id="set-dup"
              label="Allow duplicate messages"
              description="Permit sending the same content to the same number more than once."
              checked={!!setting.allowedDuplicate}
              disabled={saving}
              onChange={(checked) => handleToggle("allowedDuplicate", checked)}
            />
            <ToggleBar
              id="set-pin-instance"
              label="Pin sends to instance"
              description="ON: only send from the selected/related instance (no failover or campaign switching). OFF: allow failover and multi-instance campaign rotation."
              checked={!!setting.pin_instance_send}
              disabled={saving}
              onChange={(checked) => handleToggle("pin_instance_send", checked)}
            />
          </section>

          <section className="settings-card">
            <div className="settings-card-head">
              <div className="settings-card-icon sky">
                <Icon icon="mdi:message-text-outline" width={22} />
              </div>
              <div>
                <h3>Inbox experience</h3>
                <p>Tune the live chat workspace to match how your team replies.</p>
              </div>
            </div>
            <ToggleBar
              id="set-chat"
              label="Enable chat inbox"
              description="Show WhatsApp / Facebook / Instagram conversations in the sidebar."
              checked={!!setting.chat_enabled}
              disabled={saving}
              onChange={(checked) => handleToggle("chat_enabled", checked)}
            />
            <ToggleBar
              id="set-sound"
              label="Inbox notification sound"
              description="Play a soft sound when a new customer message arrives."
              checked={!!setting.inbox_sound}
              disabled={saving}
              onChange={(checked) => handleToggle("inbox_sound", checked)}
            />
            <ToggleBar
              id="set-enter"
              label="Enter to send"
              description="When on, Enter sends the message. When off, only the send button sends."
              checked={!!setting.enter_to_send}
              disabled={saving}
              onChange={(checked) => handleToggle("enter_to_send", checked)}
            />
          </section>

          <section className="settings-card">
            <div className="settings-card-head">
              <div className="settings-card-icon amber">
                <Icon icon="mdi:bell-ring-outline" width={22} />
              </div>
              <div>
                <h3>Alerts &amp; notifications</h3>
                <p>Show alerts in the header bell and optionally email you.</p>
              </div>
            </div>
            <ToggleBar
              id="set-in-app"
              label="Header notifications"
              description="ON: show session and campaign alerts in the top-right notification bell."
              checked={!!alerts.alert_in_app}
              disabled={saving}
              onChange={(checked) => handleAlertToggle("alert_in_app", checked)}
            />
            <ToggleBar
              id="set-disconnect"
              label="Session disconnect alerts"
              description="Notify when a linked WhatsApp session goes offline."
              checked={!!alerts.alert_disconnect}
              disabled={saving}
              onChange={(checked) => handleAlertToggle("alert_disconnect", checked)}
            />
            <ToggleBar
              id="set-campaign-fail"
              label="Campaign failure alerts"
              description="Notify when a campaign finishes with failed sends or runs out of credits."
              checked={!!alerts.alert_campaign_fail}
              disabled={saving}
              onChange={(checked) => handleAlertToggle("alert_campaign_fail", checked)}
            />
            <div className="settings-field">
              <label htmlFor="alert-email">Alert email</label>
              <input
                id="alert-email"
                type="email"
                className="form-control"
                placeholder="Defaults to your account email"
                value={alerts.alert_email}
                onChange={(e) => setAlerts({ ...alerts, alert_email: e.target.value })}
              />
            </div>
            <div className="settings-actions">
              <button
                type="button"
                className="btn btn-set-primary btn-sm"
                disabled={saving}
                onClick={() => saveAlerts(alerts)}
              >
                Save notification settings
              </button>
            </div>
          </section>
        </div>

        <div className="settings-stack">
          <section className="settings-card">
            <div className="settings-card-head">
              <div className="settings-card-icon rose">
                <Icon icon="mdi:shield-account-outline" width={22} />
              </div>
              <div>
                <h3>Account & security</h3>
                <p>Update your name, email, or password.</p>
              </div>
            </div>
            <form onSubmit={saveAccount}>
              <div className="settings-field">
                <label htmlFor="set-name">Display name</label>
                <input
                  id="set-name"
                  className="form-control"
                  value={security.name}
                  onChange={(e) => setSecurity({ ...security, name: e.target.value })}
                  required
                />
              </div>
              <div className="settings-field">
                <label htmlFor="set-email">Login email</label>
                <input
                  id="set-email"
                  type="email"
                  className="form-control"
                  value={security.email}
                  onChange={(e) => setSecurity({ ...security, email: e.target.value })}
                  required
                />
              </div>
              <div className="settings-field">
                <label htmlFor="set-pass">New password</label>
                <input
                  id="set-pass"
                  type="password"
                  className="form-control"
                  placeholder="Leave blank to keep current"
                  value={security.newpass}
                  onChange={(e) => setSecurity({ ...security, newpass: e.target.value })}
                  autoComplete="new-password"
                />
              </div>
              <div className="settings-field">
                <label htmlFor="set-pass2">Confirm password</label>
                <input
                  id="set-pass2"
                  type="password"
                  className="form-control"
                  value={security.confirm}
                  onChange={(e) => setSecurity({ ...security, confirm: e.target.value })}
                  autoComplete="new-password"
                />
              </div>
              <div className="settings-actions">
                <button type="submit" className="btn btn-set-primary btn-sm" disabled={saving}>
                  Save account
                </button>
                <Link to="/my-profile" className="btn btn-light btn-sm">
                  Full profile & photo
                </Link>
              </div>
            </form>
          </section>

          <section className="settings-card">
            <div className="settings-card-head">
              <div className="settings-card-icon slate">
                <Icon icon="mdi:lightning-bolt-outline" width={22} />
              </div>
              <div>
                <h3>Related controls</h3>
                <p>Jump to tools that shape how customers experience your replies.</p>
              </div>
            </div>
            <div className="settings-links">
              <Link to="/automation" className="settings-link">
                <Icon icon="mdi:robot-outline" width={22} />
                <div>
                  <strong>Chatbot</strong>
                  <span>Keyword auto-replies for WhatsApp sessions</span>
                </div>
                <Icon icon="mdi:chevron-right" className="settings-link-arrow" width={20} />
              </Link>
              <Link to="/quick-replies" className="settings-link">
                <Icon icon="mdi:flash-outline" width={22} />
                <div>
                  <strong>Quick replies</strong>
                  <span>Canned snippets for the inbox composer</span>
                </div>
                <Icon icon="mdi:chevron-right" className="settings-link-arrow" width={20} />
              </Link>
              <Link to="/api-access" className="settings-link">
                <Icon icon="mdi:key-variant" width={22} />
                <div>
                  <strong>API keys</strong>
                  <span>Generate keys for programmatic sends</span>
                </div>
                <Icon icon="mdi:chevron-right" className="settings-link-arrow" width={20} />
              </Link>
              <Link to="/webhook-settings" className="settings-link">
                <Icon icon="mdi:webhook" width={22} />
                <div>
                  <strong>Webhooks</strong>
                  <span>Facebook / Instagram messenger config</span>
                </div>
                <Icon icon="mdi:chevron-right" className="settings-link-arrow" width={20} />
              </Link>
              <Link to="/instances" className="settings-link">
                <Icon icon="mdi:whatsapp" width={22} />
                <div>
                  <strong>Sessions</strong>
                  <span>Reconnect or manage WhatsApp instances</span>
                </div>
                <Icon icon="mdi:chevron-right" className="settings-link-arrow" width={20} />
              </Link>
              <Link to="/support" className="settings-link">
                <Icon icon="mdi:lifebuoy" width={22} />
                <div>
                  <strong>Support</strong>
                  <span>Open an issue and live-chat with our team</span>
                </div>
                <Icon icon="mdi:chevron-right" className="settings-link-arrow" width={20} />
              </Link>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default SettingLayer;
